import { ExternalLink, Calendar, Sparkles, Users2, HeartHandshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/lib/api";

const PILLARS = [
  { icon: Calendar, label: "Responsive scheduling" },
  { icon: Sparkles, label: "Professional presentation" },
  { icon: Users2, label: "Reliable support staff" },
  { icon: HeartHandshake, label: "Patient-centered service" },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="bg-white border-t border-slate-200 py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left: title + headline */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-teal flex items-center gap-2 mb-5">
              <span className="h-px w-8 bg-brand-gold" /> Why Choose Us
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] font-semibold text-slate-900 tracking-tight leading-[1.12]">
              Professional mobile healthcare support designed around{" "}
              <span className="text-brand-teal">reliability</span>,{" "}
              <span className="text-brand-teal">responsiveness</span>, and{" "}
              <span className="text-brand-teal">patient care</span>.
            </h2>

            <div className="mt-8 h-px w-24 bg-brand-gold" aria-hidden="true" />

            <ul
              className="mt-8 grid grid-cols-2 gap-3"
              data-testid="why-us-pillars"
            >
              {PILLARS.map(({ icon: Icon, label }) => (
                <li
                  key={label}
                  className="flex items-start gap-2.5 text-sm text-slate-700"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center bg-amber-50 text-brand-gold rounded-sm shrink-0 mt-0.5">
                    <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
                  </span>
                  <span className="leading-snug">{label}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: body + CTA */}
          <div className="lg:col-span-7" data-testid="why-us-body">
            <div className="space-y-5 text-slate-600 leading-relaxed text-base md:text-lg">
              <p>
                T&amp;J Clinical Solutions provides dependable healthcare
                support services focused on{" "}
                <span className="text-slate-900 font-medium">
                  professionalism, organization, communication, and
                  compassionate patient interaction
                </span>
                .
              </p>
              <p>
                Our services are designed to support individuals, healthcare
                providers, facilities, and organizations with flexible mobile
                healthcare solutions across New Jersey and surrounding service
                areas.
              </p>
              <p>
                We focus on creating smooth healthcare experiences through
                responsive scheduling, professional presentation, reliable
                support staff, and patient-centered service. From mobile
                healthcare support to facility partnerships and healthcare
                coordination, our goal is to make healthcare access more
                convenient, organized, and accessible.
              </p>
              <p className="border-l-2 border-brand-teal pl-5 text-slate-900 italic">
                Every request is handled with attention to detail — because
                every patient interaction matters.
              </p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 border border-slate-200 bg-slate-50 p-6 md:p-7 rounded-sm">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700 mb-1">
                  Partnerships
                </div>
                <h3 className="font-heading text-lg md:text-xl font-semibold text-slate-900 tracking-tight">
                  Looking to partner with us?
                </h3>
                <p className="text-sm text-slate-600 mt-1 max-w-md">
                  Facilities, providers, and organizations welcome — let's
                  build something reliable together.
                </p>
              </div>
              <Button
                asChild
                data-testid="why-us-partner-button"
                size="lg"
                className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-sm h-12 px-6 font-semibold tracking-wide whitespace-nowrap"
              >
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Partner With Us <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
